import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { resolveAssetUrl } from "@/lib/api";
import {
  LogOut,
  Upload,
  Trash2,
  Image as ImageIcon,
  Save,
  Loader2,
  ArrowLeft,
  Settings as SettingsIcon,
  Eye,
} from "lucide-react";

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: ImageIcon },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "destinations", label: "Destinations", icon: SettingsIcon },
  { key: "vehicles", label: "Vehicles", icon: SettingsIcon },
  { key: "branding", label: "Logo & Branding", icon: SettingsIcon },
  { key: "site", label: "Site Content", icon: SettingsIcon },
];

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--st-bg)] text-[var(--st-ink)]">
      <header className="border-b border-[var(--st-border)] bg-[var(--st-surface)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-xs uppercase tracking-[0.3em] inline-flex items-center gap-2 text-[var(--st-ink-2)] hover:text-[var(--st-ink)]" data-testid="admin-back-site">
              <ArrowLeft size={14} /> Site
            </Link>
            <span className="font-serif-display text-xl tracking-wide">SHAURYA TRAVELS · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" data-testid="admin-preview-site" className="text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:text-[var(--st-accent)]">
              <Eye size={14} /> Preview
            </Link>
            <span className="text-xs text-[var(--st-ink-2)] hidden sm:inline">{user.email}</span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 px-4 py-2 border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 py-10">
        <aside className="lg:col-span-3">
          <nav className="flex lg:flex-col gap-2 lg:gap-1" data-testid="admin-sidebar">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`text-left px-4 py-3 text-xs uppercase tracking-[0.25em] border-l-2 flex items-center gap-3 transition-colors ${
                    active
                      ? "border-[var(--st-accent)] bg-[var(--st-surface)] text-[var(--st-ink)]"
                      : "border-transparent text-[var(--st-ink-2)] hover:bg-[var(--st-surface)]/60"
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>
        <section className="lg:col-span-9">
          {tab === "enquiries" && <EnquiriesPanel />}
          {tab === "photos" && <PhotosPanel />}
          {tab === "destinations" && <DestinationsPanel />}
          {tab === "vehicles" && <VehiclesPanel />}
          {tab === "branding" && <BrandingPanel />}
          {tab === "site" && <SitePanel />}
        </section>
      </div>
    </div>
  );
};

// ------------------- Photos Panel ------------------------
const PhotosPanel = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get("/photos").then((r) => setPhotos(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onUpload = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please choose a file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCaption("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this photograph permanently?")) return;
    await api.delete(`/photos/${id}`);
    load();
  };

  return (
    <div data-testid="admin-photos-panel">
      <h2 className="font-serif-display text-3xl mb-2">Photos</h2>
      <p className="text-sm text-[var(--st-ink-2)] mb-8">Upload and manage gallery photographs.</p>

      <form onSubmit={onUpload} className="bg-[var(--st-surface)] border border-[var(--st-border)] p-6 sm:p-8 mb-10 grid grid-cols-1 sm:grid-cols-12 gap-4" data-testid="admin-upload-form">
        <div className="sm:col-span-5">
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Photograph</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="admin-upload-file"
            className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:uppercase file:tracking-[0.25em] file:bg-[var(--st-ink)] file:text-white"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <div className="sm:col-span-5">
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Caption (optional)</label>
          <input
            data-testid="admin-upload-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Sunrise over Ladakh"
            className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]"
          />
        </div>
        <div className="sm:col-span-2 flex items-end">
          <button
            type="submit"
            disabled={uploading}
            data-testid="admin-upload-submit"
            className="btn-accent w-full px-4 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading" : "Upload"}
          </button>
        </div>
        {error && <div className="sm:col-span-12 text-sm text-red-700">{String(error)}</div>}
      </form>

      {loading ? (
        <div className="text-center text-sm text-[var(--st-ink-2)] py-12">Loading…</div>
      ) : photos.length === 0 ? (
        <div data-testid="admin-photos-empty" className="border border-dashed border-[var(--st-border)] py-24 text-center text-[var(--st-ink-2)]">
          No photographs yet. Upload your first one above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative group bg-[var(--st-surface)] border border-[var(--st-border)] overflow-hidden" data-testid={`admin-photo-${p.id}`}>
              <img src={resolveAssetUrl(p.url)} alt={p.caption} className="w-full h-40 object-cover" />
              <button
                onClick={() => onDelete(p.id)}
                data-testid={`admin-delete-photo-${p.id}`}
                className="absolute top-2 right-2 p-2 bg-white/95 text-red-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
              {p.caption && (
                <div className="p-3 text-xs text-[var(--st-ink-2)] truncate">{p.caption}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------------- Branding Panel ----------------------
const BrandingPanel = () => {
  const [settings, setSettings] = useState(null);
  const [file, setFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [heroBusy, setHeroBusy] = useState(false);
  const [error, setError] = useState("");
  const [heroError, setHeroError] = useState("");

  const load = () => api.get("/settings").then((r) => setSettings(r.data));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Choose a logo image first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove current logo?")) return;
    setBusy(true);
    try {
      await api.delete("/logo");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onUploadHero = async (e) => {
    e.preventDefault();
    setHeroError("");
    if (!heroFile) { setHeroError("Choose a hero background image first"); return; }
    setHeroBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", heroFile);
      await api.post("/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setHeroFile(null);
    } catch (err) {
      setHeroError(err.response?.data?.detail || "Hero upload failed");
    } finally {
      setHeroBusy(false);
    }
  };

  const onRemoveHero = async () => {
    if (!window.confirm("Reset to default hero image?")) return;
    setHeroBusy(true);
    try {
      await api.delete("/hero-image");
      await load();
    } finally {
      setHeroBusy(false);
    }
  };

  return (
    <div data-testid="admin-branding-panel">
      <h2 className="font-serif-display text-3xl mb-2">Logo & Branding</h2>
      <p className="text-sm text-[var(--st-ink-2)] mb-8">Upload your logo and the homepage hero background.</p>

      {/* LOGO */}
      <div className="bg-[var(--st-surface)] border border-[var(--st-border)] p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-6 items-center mb-8">
        <div className="sm:col-span-3 flex justify-center">
          <div className="w-32 h-32 border border-[var(--st-border)] flex items-center justify-center bg-[var(--st-bg)]" data-testid="admin-logo-preview">
            {settings?.logo_url ? (
              <img src={resolveAssetUrl(settings.logo_url)} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs uppercase tracking-[0.25em] text-[var(--st-ink-2)]">No logo</span>
            )}
          </div>
        </div>
        <form onSubmit={onUpload} className="sm:col-span-9 space-y-4" data-testid="admin-logo-form">
          <div>
            <label className="text-xs uppercase tracking-[0.25em] opacity-70">Logo image</label>
            <input
              type="file"
              accept="image/*"
              data-testid="admin-logo-file"
              className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:uppercase file:tracking-[0.25em] file:bg-[var(--st-ink)] file:text-white"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={busy} data-testid="admin-logo-upload-btn"
              className="btn-accent px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Logo
            </button>
            {settings?.logo_url && (
              <button type="button" onClick={onRemove} data-testid="admin-logo-remove-btn" disabled={busy}
                className="px-6 py-3 text-xs uppercase tracking-[0.25em] border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition-colors inline-flex items-center gap-2">
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
          {error && <div className="text-sm text-red-700">{String(error)}</div>}
        </form>
      </div>

      {/* HERO BACKGROUND */}
      <div className="bg-[var(--st-surface)] border border-[var(--st-border)] p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-6 items-center">
        <div className="sm:col-span-4">
          <div className="aspect-video border border-[var(--st-border)] bg-[var(--st-bg)] overflow-hidden" data-testid="admin-hero-preview">
            {settings?.hero_image_url ? (
              <img src={resolveAssetUrl(settings.hero_image_url)} alt="Hero" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs uppercase tracking-[0.25em] text-[var(--st-ink-2)]">Using default image</div>
            )}
          </div>
        </div>
        <form onSubmit={onUploadHero} className="sm:col-span-8 space-y-4" data-testid="admin-hero-form">
          <div>
            <div className="text-sm font-medium mb-1">Homepage Hero Background</div>
            <p className="text-xs text-[var(--st-ink-2)] mb-3">
              This is the big background image on the home page. Upload a landscape photo (recommended 2000×1200px or larger).
            </p>
            <input
              type="file"
              accept="image/*"
              data-testid="admin-hero-file"
              className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:uppercase file:tracking-[0.25em] file:bg-[var(--st-ink)] file:text-white"
              onChange={(e) => setHeroFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={heroBusy} data-testid="admin-hero-upload-btn"
              className="btn-accent px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60">
              {heroBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Hero Image
            </button>
            {settings?.hero_image_url && (
              <button type="button" onClick={onRemoveHero} data-testid="admin-hero-remove-btn" disabled={heroBusy}
                className="px-6 py-3 text-xs uppercase tracking-[0.25em] border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition-colors inline-flex items-center gap-2">
                <Trash2 size={14} /> Use Default
              </button>
            )}
          </div>
          {heroError && <div className="text-sm text-red-700">{String(heroError)}</div>}
        </form>
      </div>
    </div>
  );
};

// ------------------- Site Content Panel ------------------
const SitePanel = () => {
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const onChange = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    setSaved(false);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <div className="text-sm text-[var(--st-ink-2)]">Loading…</div>;

  const FIELDS = [
    { key: "hero_title", label: "Hero Title" },
    { key: "hero_subtitle", label: "Hero Subtitle" },
    { key: "about_title", label: "About — Title" },
    { key: "about_text", label: "About — Text", textarea: true },
    { key: "contact_email", label: "Contact Email" },
    { key: "contact_phone", label: "Contact Phone" },
    { key: "contact_address", label: "Address" },
  ];

  return (
    <form onSubmit={onSave} data-testid="admin-site-panel" className="space-y-6">
      <div>
        <h2 className="font-serif-display text-3xl mb-2">Site Content</h2>
        <p className="text-sm text-[var(--st-ink-2)] mb-2">Edit homepage, about, and contact details.</p>
      </div>
      <div className="bg-[var(--st-surface)] border border-[var(--st-border)] p-6 sm:p-8 space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs uppercase tracking-[0.25em] opacity-70">{f.label}</label>
            {f.textarea ? (
              <textarea
                rows={5}
                data-testid={`admin-field-${f.key}`}
                value={settings[f.key] || ""}
                onChange={onChange(f.key)}
                className="mt-2 w-full bg-transparent border border-[var(--st-border)] p-3 focus:outline-none focus:border-[var(--st-accent)] resize-none"
              />
            ) : (
              <input
                data-testid={`admin-field-${f.key}`}
                value={settings[f.key] || ""}
                onChange={onChange(f.key)}
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)]"
              />
            )}
          </div>
        ))}
        {error && <div className="text-sm text-red-700">{String(error)}</div>}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-site-save-btn"
            className="btn-accent px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </button>
          {saved && (
            <span data-testid="admin-site-saved" className="text-xs uppercase tracking-[0.25em] text-[var(--st-accent)]">Saved ✓</span>
          )}
        </div>
      </div>
    </form>
  );
};

export default AdminDashboard;

// ------------------- Enquiries Panel ---------------------
function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/enquiries").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/enquiries/${id}`);
    load();
  };

  return (
    <div data-testid="admin-enquiries-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl mb-1">Enquiries</h2>
          <p className="text-sm text-[var(--st-ink-2)]">Leads submitted from the contact form.</p>
        </div>
        <span className="text-xs uppercase tracking-[0.25em] text-[var(--st-ink-2)]" data-testid="admin-enquiries-count">
          {items.length} total
        </span>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[var(--st-ink-2)] py-12">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="admin-enquiries-empty" className="border border-dashed border-[var(--st-border)] py-24 text-center text-[var(--st-ink-2)]">
          No enquiries yet. They'll appear here when visitors submit the contact form.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((e) => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="bg-[var(--st-surface)] border border-[var(--st-border)] p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--st-accent)] mb-1">
                    {new Date(e.created_at).toLocaleString()}
                    {e.destination ? `  ·  ${e.destination}` : ""}
                  </div>
                  <div className="font-serif-display text-2xl">{e.name}</div>
                  <div className="text-sm text-[var(--st-ink-2)] mt-1 flex flex-wrap gap-4">
                    <a className="hover:text-[var(--st-accent)]" href={`tel:${e.phone}`}>📞 {e.phone}</a>
                    <a className="hover:text-[var(--st-accent)]" href={`https://wa.me/${e.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">💬 WhatsApp</a>
                    {e.email && <a className="hover:text-[var(--st-accent)]" href={`mailto:${e.email}`}>✉ {e.email}</a>}
                  </div>
                  {e.message && (
                    <p className="text-sm text-[var(--st-ink)] mt-3 leading-relaxed whitespace-pre-wrap">{e.message}</p>
                  )}
                </div>
                <button
                  onClick={() => onDelete(e.id)}
                  data-testid={`admin-delete-enquiry-${e.id}`}
                  className="text-xs px-3 py-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition uppercase tracking-[0.2em] inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------- Destinations Panel ------------------
function DestinationsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // destination obj or "new" or null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/destinations").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const onSave = async (form, imageFile) => {
    setError("");
    setBusy(true);
    try {
      const payload = { ...form, tags: (form.tagsCsv || "").split(",").map((t) => t.trim()).filter(Boolean) };
      delete payload.tagsCsv;
      let saved;
      if (editing === "new") {
        const r = await api.post("/destinations", payload);
        saved = r.data;
      } else {
        const r = await api.put(`/destinations/${editing.id}`, payload);
        saved = r.data;
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/destinations/${saved.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await load();
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this destination?")) return;
    await api.delete(`/destinations/${id}`);
    load();
  };

  return (
    <div data-testid="admin-destinations-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl mb-1">Destinations</h2>
          <p className="text-sm text-[var(--st-ink-2)]">Add, edit, and showcase destinations.</p>
        </div>
        <button
          data-testid="admin-add-destination-btn"
          onClick={() => setEditing("new")}
          className="btn-accent px-5 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2"
        >
          <Upload size={14} /> Add Destination
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <div key={d.id} data-testid={`admin-destination-${d.id}`} className="bg-[var(--st-surface)] border border-[var(--st-border)] overflow-hidden">
            <div className="aspect-[4/3] bg-[var(--st-bg)] overflow-hidden">
              {d.image_url ? (
                <img src={resolveAssetUrl(d.image_url)} alt={d.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[var(--st-ink-2)] uppercase tracking-[0.2em]">No image</div>
              )}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--st-accent)]">{d.region}</div>
              <div className="font-serif-display text-xl mt-1 mb-1">{d.name}</div>
              <p className="text-xs text-[var(--st-ink-2)] line-clamp-2 mb-3">{d.short_description}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(d)} data-testid={`admin-edit-destination-${d.id}`} className="text-xs px-3 py-2 border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition uppercase tracking-[0.2em]">Edit</button>
                <button onClick={() => onDelete(d.id)} data-testid={`admin-delete-destination-${d.id}`} className="text-xs px-3 py-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition uppercase tracking-[0.2em] inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <DestinationFormModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

function DestinationFormModal({ initial, onClose, onSave, busy, error }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    region: initial?.region || "",
    short_description: initial?.short_description || "",
    description: initial?.description || "",
    duration: initial?.duration || "",
    starting_price: initial?.starting_price || "",
    tagsCsv: (initial?.tags || []).join(", "),
  }));
  const [file, setFile] = useState(null);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); onSave(form, file); };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-destination-form"
        className="bg-[var(--st-bg)] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--st-border)] p-6 sm:p-8 space-y-4"
      >
        <h3 className="font-serif-display text-2xl">{initial ? "Edit Destination" : "Add Destination"}</h3>
        {["name", "region", "duration", "starting_price"].map((k) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-[0.25em] opacity-70">{k.replace("_", " ")}</label>
            <input data-testid={`admin-dest-${k}`} value={form[k]} onChange={handle(k)} className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]" />
          </div>
        ))}
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Short Description</label>
          <input data-testid="admin-dest-short_description" value={form.short_description} onChange={handle("short_description")} className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Full Description</label>
          <textarea rows={4} data-testid="admin-dest-description" value={form.description} onChange={handle("description")} className="mt-2 w-full bg-transparent border border-[var(--st-border)] p-3 focus:outline-none focus:border-[var(--st-accent)] resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Highlights (comma-separated)</label>
          <input data-testid="admin-dest-tagsCsv" value={form.tagsCsv} onChange={handle("tagsCsv")} placeholder="e.g. Mountains, Adventure, Culture" className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Image (optional — upload new)</label>
          <input type="file" accept="image/*" data-testid="admin-dest-file" className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:uppercase file:tracking-[0.25em] file:bg-[var(--st-ink)] file:text-white" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        {error && <div className="text-sm text-red-700">{String(error)}</div>}
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-5 py-3 text-xs uppercase tracking-[0.25em] border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition">Cancel</button>
          <button type="submit" disabled={busy} data-testid="admin-dest-save-btn" className="btn-accent px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

// ------------------- Vehicles Panel ----------------------
function VehiclesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/vehicles").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const onSave = async (form, imageFile) => {
    setError("");
    setBusy(true);
    try {
      const payload = { ...form, features: (form.featuresCsv || "").split(",").map((t) => t.trim()).filter(Boolean) };
      delete payload.featuresCsv;
      let saved;
      if (editing === "new") {
        const r = await api.post("/vehicles", payload);
        saved = r.data;
      } else {
        const r = await api.put(`/vehicles/${editing.id}`, payload);
        saved = r.data;
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/vehicles/${saved.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await load();
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    await api.delete(`/vehicles/${id}`);
    load();
  };

  return (
    <div data-testid="admin-vehicles-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl mb-1">Vehicles</h2>
          <p className="text-sm text-[var(--st-ink-2)]">Manage your fleet — cars, SUVs, mini-buses, coaches.</p>
        </div>
        <button
          data-testid="admin-add-vehicle-btn"
          onClick={() => setEditing("new")}
          className="btn-accent px-5 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2"
        >
          <Upload size={14} /> Add Vehicle
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((v) => (
          <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="bg-[var(--st-surface)] border border-[var(--st-border)] overflow-hidden">
            <div className="aspect-[4/3] bg-[var(--st-bg)] overflow-hidden">
              {v.image_url ? (
                <img src={resolveAssetUrl(v.image_url)} alt={v.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[var(--st-ink-2)] uppercase tracking-[0.2em]">No image</div>
              )}
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--st-accent)]">{v.type} {v.seating ? `· ${v.seating}` : ""}</div>
              <div className="font-serif-display text-xl mt-1 mb-1">{v.name}</div>
              <p className="text-xs text-[var(--st-ink-2)] line-clamp-2 mb-3">{v.description}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(v)} data-testid={`admin-edit-vehicle-${v.id}`} className="text-xs px-3 py-2 border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition uppercase tracking-[0.2em]">Edit</button>
                <button onClick={() => onDelete(v.id)} data-testid={`admin-delete-vehicle-${v.id}`} className="text-xs px-3 py-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition uppercase tracking-[0.2em] inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <VehicleFormModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

function VehicleFormModal({ initial, onClose, onSave, busy, error }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    type: initial?.type || "",
    seating: initial?.seating || "",
    description: initial?.description || "",
    featuresCsv: (initial?.features || []).join(", "),
  }));
  const [file, setFile] = useState(null);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); onSave(form, file); };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-vehicle-form"
        className="bg-[var(--st-bg)] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--st-border)] p-6 sm:p-8 space-y-4"
      >
        <h3 className="font-serif-display text-2xl">{initial ? "Edit Vehicle" : "Add Vehicle"}</h3>
        {["name", "type", "seating"].map((k) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-[0.25em] opacity-70">{k}</label>
            <input data-testid={`admin-veh-${k}`} value={form[k]} onChange={handle(k)} className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]" />
          </div>
        ))}
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Description</label>
          <textarea rows={4} data-testid="admin-veh-description" value={form.description} onChange={handle("description")} className="mt-2 w-full bg-transparent border border-[var(--st-border)] p-3 focus:outline-none focus:border-[var(--st-accent)] resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Features (comma-separated)</label>
          <input data-testid="admin-veh-featuresCsv" value={form.featuresCsv} onChange={handle("featuresCsv")} placeholder="e.g. Air-conditioned, Reclining seats" className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-2 focus:outline-none focus:border-[var(--st-accent)]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.25em] opacity-70">Image (optional)</label>
          <input type="file" accept="image/*" data-testid="admin-veh-file" className="mt-2 w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:text-xs file:uppercase file:tracking-[0.25em] file:bg-[var(--st-ink)] file:text-white" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        {error && <div className="text-sm text-red-700">{String(error)}</div>}
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-5 py-3 text-xs uppercase tracking-[0.25em] border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition">Cancel</button>
          <button type="submit" disabled={busy} data-testid="admin-veh-save-btn" className="btn-accent px-6 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

